import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Settings() {
  const { data, refetch } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/admin/settings")).data });
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const s = form || data;
  if (!s) return <p className="text-sm text-slate-500">Loading…</p>;

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", {
        platform_domain: s.platform_domain,
        buffers: s.buffers,
        indiafast: s.indiafast,
        ai_limits: s.ai_limits,
        ai: s.ai,
        marketplace_config: s.marketplace_config,
      });
      toast.success("Settings saved");
      setForm(null);
      refetch();
    } catch {
      toast.error("Could not save settings");
    } finally {
      setBusy(false);
    }
  };

  const set = (k, v) => setForm({ ...s, [k]: v });
  const setBuf = (k, v) => setForm({ ...s, buffers: { ...s.buffers, [k]: Number(v) } });
  const setInd = (k, v) => setForm({ ...s, indiafast: { ...s.indiafast, [k]: v } });

  return (
    <div data-testid="settings-page" className="max-w-2xl">
      <PageHeader title="Settings" sub="Platform configuration and integration credentials." testid="settings-title" />

      <div className="space-y-4">
        <div className="rounded-xl border border-slate-800 bg-[#111827] p-5">
          <h3 className="mb-4 text-sm font-semibold text-slate-200">Platform</h3>
          <Label className="text-xs text-slate-400">Platform domain (store subdomains)</Label>
          <Input data-testid="settings-platform-domain" value={s.platform_domain || ""} onChange={(e) => set("platform_domain", e.target.value)}
            className="mt-1.5 border-slate-700 bg-slate-900 text-slate-100" />
          <p className="mt-2 text-[11px] text-slate-500">Stores are served at /store/{"{slug}"} in this environment; custom domains map via the domain layer.</p>
        </div>

        <div className="rounded-xl border border-slate-800 bg-[#111827] p-5">
          <h3 className="mb-1 text-sm font-semibold text-slate-200">Inventory Buffers</h3>
          <p className="mb-4 text-[11px] text-slate-500">Reserved units per channel to prevent overselling.</p>
          <div className="grid grid-cols-3 gap-3">
            {["amazon", "flipkart", "store"].map((ch) => (
              <div key={ch}>
                <Label className="text-xs capitalize text-slate-400">{ch === "store" ? "Own Store" : ch}</Label>
                <Input data-testid={`settings-buffer-${ch}`} type="number" min={0} value={s.buffers?.[ch] ?? 0}
                  onChange={(e) => setBuf(ch, e.target.value)} className="mt-1.5 border-slate-700 bg-slate-900 text-slate-100" />
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-slate-800 bg-[#111827] p-5">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-200">IndiaFast Shipping API</h3>
            <span data-testid="indiafast-mode" className="rounded-full border border-violet-500/40 bg-violet-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase text-violet-300">
              {s.indiafast?.mode === "live" ? "LIVE" : "DEMO MODE"}
            </span>
          </div>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-400">Mode</Label>
              <div className="mt-1.5 flex gap-2">
                {["demo", "live"].map((m) => (
                  <button key={m} data-testid={`indiafast-mode-${m}`} onClick={() => setInd("mode", m)}
                    className={`rounded-lg border px-4 py-1.5 text-xs font-medium capitalize transition-colors ${s.indiafast?.mode === m ? "border-orange-500 bg-orange-500/10 text-orange-400" : "border-slate-700 text-slate-400"}`}>
                    {m}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-400">API Base URL</Label>
              <Input data-testid="indiafast-base-url" value={s.indiafast?.base_url || ""} onChange={(e) => setInd("base_url", e.target.value)}
                placeholder="https://api.indiafast.example" className="mt-1.5 border-slate-700 bg-slate-900 font-mono text-xs text-slate-100" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">API Key (stored securely, never shown to agents)</Label>
              <Input data-testid="indiafast-api-key" value={s.indiafast?.api_key || ""} onChange={(e) => setInd("api_key", e.target.value)}
                placeholder="••••••••" className="mt-1.5 border-slate-700 bg-slate-900 font-mono text-xs text-slate-100" />
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-800 bg-[#111827] p-5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-slate-200">Razorpay Payments</h3>
              <p className="mt-1 text-[11px] text-slate-500">Configured via server environment variables (RAZORPAY_KEY_ID / RAZORPAY_KEY_SECRET).</p>
            </div>
            <span data-testid="razorpay-mode" className="rounded-full border border-violet-500/40 bg-violet-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase text-violet-300">
              {s.razorpay_mode === "live" ? "LIVE" : "DEMO MODE"}
            </span>
          </div>
        </div>

        <div className="rounded-xl border border-slate-800 bg-[#111827] p-5">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-slate-200">AI Marketing Studio — Cost Control</h3>
              <p className="mt-1 text-[11px] text-slate-500">Per-agent generation limits. Provider configured server-side (EMERGENT_LLM_KEY).</p>
            </div>
            <span data-testid="ai-mode-badge" className="rounded-full border border-violet-500/40 bg-violet-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase text-violet-300">
              {s.ai?.mode === "live" ? "LIVE" : "DEMO MODE"}
            </span>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-slate-400">Daily / agent</Label>
              <Input data-testid="ai-daily-limit" type="number" min={1} value={s.ai_limits?.daily_per_agent ?? 50}
                onChange={(e) => set("ai_limits", { ...s.ai_limits, daily_per_agent: Number(e.target.value) })}
                className="mt-1.5 border-slate-700 bg-slate-900 text-slate-100" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Monthly / agent</Label>
              <Input data-testid="ai-monthly-limit" type="number" min={1} value={s.ai_limits?.monthly_per_agent ?? 500}
                onChange={(e) => set("ai_limits", { ...s.ai_limits, monthly_per_agent: Number(e.target.value) })}
                className="mt-1.5 border-slate-700 bg-slate-900 text-slate-100" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Provider mode</Label>
              <div className="mt-1.5 flex gap-2">
                {["live", "demo"].map((m) => (
                  <button key={m} data-testid={`ai-mode-${m}`} onClick={() => set("ai", { ...s.ai, mode: m })}
                    className={`flex-1 rounded-lg border px-3 py-2 text-xs font-medium capitalize transition-colors ${(s.ai?.mode || "live") === m ? "border-orange-500 bg-orange-500/10 text-orange-400" : "border-slate-700 text-slate-400"}`}>
                    {m}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-800 bg-[#111827] p-5" data-testid="marketplace-credentials-card">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-slate-200">Marketplace App Credentials</h3>
              <p className="mt-1 text-[11px] text-slate-500">Server-side only, stored encrypted. Agents authorize via OAuth — they never see these.</p>
            </div>
          </div>
          <div className="space-y-5">
            <div>
              <div className="mb-2 flex items-center gap-2">
                <p className="text-xs font-semibold text-slate-300">Amazon SP-API (India)</p>
                {s.env_configured?.amazon && <span className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[9px] font-bold uppercase text-emerald-400">Configured via server</span>}
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {[["application_id", "Application ID"], ["client_id", "LWA Client ID"], ["client_secret", "LWA Client Secret"],
                  ["aws_access_key", "AWS Access Key ID (live)"], ["aws_secret_key", "AWS Secret Access Key (live)"], ["sandbox_token", "Sandbox Token (optional)"]].map(([k, label]) => (
                  <div key={k}>
                    <Label className="text-xs text-slate-400">{label}</Label>
                    <Input data-testid={`mpcfg-amazon-${k}`} value={s.marketplace_config?.amazon?.[k] || ""}
                      onChange={(e) => set("marketplace_config", { ...s.marketplace_config, amazon: { ...(s.marketplace_config?.amazon || {}), [k]: e.target.value } })}
                      placeholder={k.includes("secret") || k.includes("token") ? "••••••••" : ""}
                      className="mt-1.5 border-slate-700 bg-slate-900 font-mono text-xs text-slate-100" />
                  </div>
                ))}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <Label className="text-xs text-slate-400">Mode:</Label>
                {["sandbox", "live"].map((m) => (
                  <button key={m} data-testid={`mpcfg-amazon-mode-${m}`}
                    onClick={() => set("marketplace_config", { ...s.marketplace_config, amazon: { ...(s.marketplace_config?.amazon || {}), sandbox: m === "sandbox" } })}
                    className={`rounded-lg border px-3 py-1 text-xs font-medium capitalize transition-colors ${(s.marketplace_config?.amazon?.sandbox ?? true) === (m === "sandbox") ? "border-orange-500 bg-orange-500/10 text-orange-400" : "border-slate-700 text-slate-400"}`}>
                    {m}
                  </button>
                ))}
              </div>
            </div>
            <div className="border-t border-slate-800 pt-4">
              <div className="mb-2 flex items-center gap-2">
                <p className="text-xs font-semibold text-slate-300">Flipkart Seller API</p>
                {s.env_configured?.flipkart && <span className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[9px] font-bold uppercase text-emerald-400">Configured via server</span>}
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {[["client_id", "Client ID (appId)"], ["client_secret", "Client Secret (appSecret)"]].map(([k, label]) => (
                  <div key={k}>
                    <Label className="text-xs text-slate-400">{label}</Label>
                    <Input data-testid={`mpcfg-flipkart-${k}`} value={s.marketplace_config?.flipkart?.[k] || ""}
                      onChange={(e) => set("marketplace_config", { ...s.marketplace_config, flipkart: { ...(s.marketplace_config?.flipkart || {}), [k]: e.target.value } })}
                      placeholder={k.includes("secret") ? "••••••••" : ""}
                      className="mt-1.5 border-slate-700 bg-slate-900 font-mono text-xs text-slate-100" />
                  </div>
                ))}
              </div>
              <p className="mt-2 text-[10px] text-slate-500">Register callback: <span className="font-mono text-slate-400">{window.location.origin}/api/marketplace/flipkart/callback</span></p>
              <p className="text-[10px] text-slate-500">Amazon redirect URI: <span className="font-mono text-slate-400">{window.location.origin}/api/marketplace/amazon/callback</span></p>
            </div>
          </div>
        </div>

        <Button data-testid="settings-save-btn" onClick={save} disabled={busy} className="bg-orange-500 font-semibold text-white hover:bg-orange-600">
          {busy ? "Saving…" : "Save settings"}
        </Button>
      </div>
    </div>
  );
}
